import React, { useState, useEffect } from "react";
import InfoWithTooltip from "./InfoWithTooltip";
import GoogleStatusBlock from "./GoogleStatusBlock";

const microsToDollars = (micros) =>
  micros == null ? "" : (Number(micros) / 1_000_000).toFixed(2);
// Google Ads requires whole-dollar bids only (1 USD = 1e6 micros). Floor so e.g. $3.25 or $3.20 → $3.
const MICROS_PER_DOLLAR = 1_000_000;
const dollarsToMicros = (dollars) => {
  if (dollars === "" || dollars == null) return null;
  const micros = Math.round(parseFloat(dollars) * MICROS_PER_DOLLAR);
  const wholeDollars = Math.floor(micros / MICROS_PER_DOLLAR) * MICROS_PER_DOLLAR;
  return wholeDollars <= 0 ? null : wholeDollars;
};

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

const apiHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-CSRF-Token": getCsrfToken(),
});

// Map Ad group field labels to ai_explanations keys from Stage 3b
const AD_GROUP_EXPLANATION_KEYS = {
  "Ad group name": "ad_group.name",
  "Type": "ad_group.type",
  "CPC bid (USD)": "ad_group.cpc_bid_micros",
  "Keywords (comma- or semicolon-separated)": "ad_group.keywords",
  Keywords: "ad_group.keywords",
};

const displayValue = (v) => (v != null && v !== "" ? String(v) : "—");

const GoogleAdsTargetingPanel = ({ campaign, onRefetch, toast }) => {
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [form, setForm] = useState({ name: "", type: "", cpc_bid_dollars: "", keywords: "" });
  const [saving, setSaving] = useState(false);
  const [syncingStatusAdGroupId, setSyncingStatusAdGroupId] = useState(null);

  const adGroups = campaign?.ad_groups || [];
  const explanations = campaign?.ai_explanations || {};
  const isAdGroupDraft = (ag) => (ag?.status || "").toLowerCase() === "draft";

  const getExplanation = (label) => {
    const key = AD_GROUP_EXPLANATION_KEYS[label];
    return key ? explanations[key] : null;
  };

  const FieldLabel = ({ label }) => (
    <span className="ga-overview-panel__field-label-wrap">
      <span className="cr-field__label">{label}</span>
      {getExplanation(label) && <InfoWithTooltip text={getExplanation(label)} />}
    </span>
  );

  // When user expands a row, load that ad group into form
  useEffect(() => {
    if (expandedIndex >= 0 && adGroups[expandedIndex]) {
      const ag = adGroups[expandedIndex];
      setForm({
        name: ag.name || "",
        type: ag.type || "",
        cpc_bid_dollars: microsToDollars(ag.cpc_bid_micros),
        keywords: Array.isArray(ag.keywords) ? ag.keywords.join(", ") : "",
      });
    }
  }, [expandedIndex, adGroups]);

  const parseKeywords = (str) =>
    str
      .split(/[,;]/)
      .map((k) => k.trim())
      .filter(Boolean);

  const saveAdGroup = async () => {
    const ag = expandedIndex >= 0 ? adGroups[expandedIndex] : null;
    if (!ag?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/google_ads/ad_groups/${ag.id}`, {
        method: "PATCH",
        headers: apiHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          type: (form.type || "").trim() || undefined,
          cpc_bid_micros: dollarsToMicros(form.cpc_bid_dollars),
          keywords: parseKeywords(form.keywords),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast?.success("Ad group updated.");
        onRefetch?.();
      } else {
        toast?.error(data.errors?.join(" ") || "Failed to update ad group");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveAdGroup();
  };

  const handleSyncAdGroupStatus = async (adGroupId) => {
    if (!adGroupId) return;
    setSyncingStatusAdGroupId(adGroupId);
    try {
      const res = await fetch(`/api/google_ads/ad_groups/${adGroupId}/sync_status`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data?.id) {
        onRefetch?.();
        toast?.success("Ad group and campaign status synced.");
      } else {
        toast?.error(data?.error || "Failed to sync status.");
      }
    } catch (e) {
      toast?.error("Failed to sync status.");
      console.error(e);
    } finally {
      setSyncingStatusAdGroupId(null);
    }
  };

  if (!campaign) return null;

  if (adGroups.length === 0) {
    return (
      <div className="cr-panel cr-panel--targeting">
        <div className="cr-panel__empty">
          <p>No ad groups available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cr-panel cr-panel--targeting">
      <div className="cr-panel__section">
        <h3 className="cr-panel__section-title">Ad group settings</h3>
      </div>
      <div className="cr-adsets">
        {adGroups.map((ag, index) => (
          <div
            key={ag.id}
            className={`cr-adset ${expandedIndex === index ? "cr-adset--expanded" : ""}`}
          >
            <button
              type="button"
              className="cr-adset__header"
              onClick={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
            >
              <div className="cr-adset__header-left">
                <h4 className="cr-adset__name">{ag.name}</h4>
                <span className="cr-adset__ads-count">
                  CPC {microsToDollars(ag.cpc_bid_micros) || "—"} · {(ag.keywords || []).length} keyword(s) · {ag.ads?.length ?? 0} ad(s)
                </span>
              </div>
              <div className="cr-adset__header-right">
                <svg
                  className="cr-adset__chevron"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ transform: expandedIndex === index ? "rotate(180deg)" : "none", flexShrink: 0 }}
                >
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </button>

            {expandedIndex === index && (
              <div className="cr-adset__content">
                {isAdGroupDraft(ag) ? (
                  <form onSubmit={handleSubmit} className="cr-panel__fields" style={{ width: "100%" }}>
                    <div className="cr-field">
                      <div className="cr-field__header">
                        <FieldLabel label="Ad group name" />
                      </div>
                      <div className="cr-field__value">
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="e.g. Search - Keywords"
                          style={{ width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 14 }}
                          required
                        />
                      </div>
                    </div>
                    <div className="cr-field">
                      <div className="cr-field__header">
                        <span className="cr-field__label">Type</span>
                      </div>
                      <div className="cr-field__value">
                        <input
                          type="text"
                          value={form.type}
                          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                          placeholder="e.g. SEARCH_STANDARD"
                          style={{ width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 14 }}
                        />
                      </div>
                    </div>
                    <div className="cr-field">
                      <div className="cr-field__header">
                        <FieldLabel label="CPC bid (USD)" />
                      </div>
                      <div className="cr-field__value">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form.cpc_bid_dollars}
                          onChange={(e) => setForm((f) => ({ ...f, cpc_bid_dollars: e.target.value }))}
                          placeholder="e.g. 1.00"
                          style={{ width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 14 }}
                        />
                      </div>
                    </div>
                    <div className="cr-field">
                      <div className="cr-field__header">
                        <FieldLabel label="Keywords (comma- or semicolon-separated)" />
                      </div>
                      <div className="cr-field__value">
                        <input
                          type="text"
                          value={form.keywords}
                          onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
                          placeholder="e.g. rails developers, ruby agency"
                          style={{ width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 14 }}
                        />
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                      <button type="submit" className="btn btn-sm ga-btn-save" disabled={saving} style={{ width: "100%" }}>
                        {saving ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="cr-panel__fields" style={{ width: "100%" }}>
                    {ag.google_ad_group_id && (
                      <GoogleStatusBlock
                        title="Google status"
                        rows={[
                          { label: "Status", value: ag.status },
                          { label: "Primary status", value: ag.primary_status },
                        ]}
                        reasons={Array.isArray(ag.primary_status_reasons) ? ag.primary_status_reasons : []}
                        onSync={() => handleSyncAdGroupStatus(ag.id)}
                        syncing={syncingStatusAdGroupId === ag.id}
                        showSyncButton
                      />
                    )}
                    <div className="cr-field">
                      <div className="cr-field__header"><FieldLabel label="Ad group name" /></div>
                      <div className="cr-field__value" style={{ fontWeight: 500 }}>{ag.name || "—"}</div>
                    </div>
                    <div className="cr-field">
                      <div className="cr-field__header"><FieldLabel label="Type" /></div>
                      <div className="cr-field__value" style={{ fontWeight: 500 }}>{ag.type || "—"}</div>
                    </div>
                    <div className="cr-field">
                      <div className="cr-field__header"><FieldLabel label="CPC bid (USD)" /></div>
                      <div className="cr-field__value" style={{ fontWeight: 500 }}>{microsToDollars(ag.cpc_bid_micros) || "—"}</div>
                    </div>
                    <div className="cr-field">
                      <div className="cr-field__header"><FieldLabel label="Keywords" /></div>
                      <div className="cr-field__value" style={{ fontWeight: 500 }}>{Array.isArray(ag.keywords) ? ag.keywords.join(", ") || "—" : "—"}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GoogleAdsTargetingPanel;
